/* eslint-disable max-lines-per-function */
import { useCallback } from 'react';

import {
   ButtonVariant,
   dispatchSnackbarNegativeEvent,
   dispatchSnackbarPositiveEvent,
   Modal,
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalMaxHeight,
   ModalWidth,
   StyledFlex,
} from '@dreryk/react-components';

import { appointmentsTranslationKeys } from '../../../translations';
import { usePreInterviewGlobalState } from '../../table/PreInterviewResolver.hooks';
import { PreInterviewApproveAction } from '../shared/actions/PreInterviewApproveAction';
import { SurveyUpdateAction } from '../shared/actions/SurveyUpdateAction';
import { PreInterviewFormLoader } from '../shared/form/PreInterviewForm.loader';
import { PatientInformationView } from '../shared/PatientInformationView/PatientInformationView';

import { PreInterviewUpdateForm } from './PreInterviewUpdateForm';
import { PreInterviewUpdateFormLoader } from './PreInterviewUpdateForm.loader';
import { formInitializer, preInterviewModalConfirmExitConfig } from './PreInterviewUpdateModal.utils';

type PreInterviewUpdateModalProps = {
   readonly appointmentId: number;
   readonly beginDate: Date;
   readonly endDate: Date;
   readonly patientFullName: string;
   readonly surveyId: number;
   readonly onClose: VoidFunction;
};

export const PreInterviewUpdateModal = (props: PreInterviewUpdateModalProps): JSX.Element => {
   const { updateSinglePreInterviewState } = usePreInterviewGlobalState();

   const handleSurveyUpdateSuccess = useCallback(() => {
      props.onClose();
      dispatchSnackbarPositiveEvent(
         appointmentsTranslationKeys.preInterview.modal.create.form.actions.save.successMessage
      );
      updateSinglePreInterviewState(props.appointmentId, { surveyId: props.surveyId, status: 'STARTED' });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.appointmentId, props.surveyId, props.onClose, updateSinglePreInterviewState]);

   const handlePreInterviewApproveSuccess = useCallback(() => {
      props.onClose();
      updateSinglePreInterviewState(props.appointmentId, { surveyId: props.surveyId, status: 'APPROVED' });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.appointmentId, props.surveyId, props.onClose, updateSinglePreInterviewState]);

   const handlePreInterviewExpiredFault = useCallback(() => {
      updateSinglePreInterviewState(props.appointmentId, { surveyId: props.surveyId, status: 'EXPIRED' });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.appointmentId, props.surveyId, updateSinglePreInterviewState]);

   const handleSurveyNotUpdatableFault = useCallback(() => {
      dispatchSnackbarNegativeEvent(
         appointmentsTranslationKeys.preInterview.modal.update.form.errors.surveyNotUpdatableFault
      );
   }, []);

   return (
      <Modal
         testId={'preInterviewUpdate'}
         title={appointmentsTranslationKeys.preInterview.modal.shared.title}
         width={ModalWidth.WIDE}
         height={ModalMaxHeight.TALL}
      >
         <StyledFlex direction={'column'} gap={24}>
            <PatientInformationView
               patientFullName={props.patientFullName}
               beginDate={props.beginDate}
               endDate={props.endDate}
            />
            <PreInterviewUpdateFormLoader surveyId={props.surveyId}>
               {({ preInterview }) => (
                  <PreInterviewFormLoader preInterviewTemplateVersionId={preInterview.surveyTemplateVersionId}>
                     {({ preInterviewTemplate }) => (
                        <PreInterviewUpdateForm
                           preInterviewTemplate={preInterviewTemplate}
                           initializer={formInitializer(preInterview)}
                        >
                           {({ handleSubmit, formState }) => (
                              <>
                                 <SurveyUpdateAction
                                    onSuccess={handleSurveyUpdateSuccess}
                                    onSurveyNotUpdatableFault={handleSurveyNotUpdatableFault}
                                 >
                                    {({ updateSurvey }) => (
                                       <ModalConfirmButton
                                          onClick={handleSubmit(async (answers) =>
                                             updateSurvey({ answers: answers, surveyId: props.surveyId })
                                          )}
                                          testId={'save'}
                                          label={
                                             appointmentsTranslationKeys.preInterview.modal.create.form.actions.save
                                                .title
                                          }
                                          variant={ButtonVariant.SECONDARY}
                                       />
                                    )}
                                 </SurveyUpdateAction>
                                 <PreInterviewApproveAction
                                    appointmentId={props.appointmentId}
                                    onSuccess={handlePreInterviewApproveSuccess}
                                    onPreInterviewExpiredFault={handlePreInterviewExpiredFault}
                                 >
                                    {({ approvePreInterview }) => (
                                       <SurveyUpdateAction
                                          onSuccess={approvePreInterview}
                                          onSurveyNotUpdatableFault={handleSurveyNotUpdatableFault}
                                       >
                                          {({ updateSurvey }) => (
                                             <ModalConfirmButton
                                                onClick={handleSubmit(async (answers) =>
                                                   updateSurvey({ answers: answers, surveyId: props.surveyId })
                                                )}
                                                testId={'confirm'}
                                                label={
                                                   appointmentsTranslationKeys.preInterview.modal.create.form.actions
                                                      .confirm.title
                                                }
                                                variant={ButtonVariant.PRIMARY}
                                             />
                                          )}
                                       </SurveyUpdateAction>
                                    )}
                                 </PreInterviewApproveAction>
                                 <ModalCancelButton
                                    onClick={props.onClose}
                                    shouldConfirmExit={preInterviewModalConfirmExitConfig(formState.isDirty)}
                                 />
                                 <ModalCloseButton
                                    onClick={props.onClose}
                                    shouldConfirmExit={preInterviewModalConfirmExitConfig(formState.isDirty)}
                                 />
                              </>
                           )}
                        </PreInterviewUpdateForm>
                     )}
                  </PreInterviewFormLoader>
               )}
            </PreInterviewUpdateFormLoader>
         </StyledFlex>
      </Modal>
   );
};
